import React, { useState, useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Toast from '../../components/UI/Toast';
import {
    ChartBarIcon,
    UsersIcon,
    BriefcaseIcon,
    ClipboardDocumentListIcon,
    CurrencyDollarIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    EyeIcon,
    ClockIcon,
    CheckCircleIcon,
    XCircleIcon,
} from '@heroicons/react/24/outline';

interface CompanyStats {
    totalJobs: number;
    activeJobs: number;
    totalApplications: number;
    pendingApplications: number;
    approvedApplications: number;
    totalTeamMembers: number;
    totalBudgetSpent: number;
    monthlySpent: number;
    jobViews: number;
    conversionRate: number;
}

interface RecentJob {
    _id: string;
    title: string;
    status: string;
    applications: number;
    views: number;
    createdAt: string;
    budgetSpent: number;
}

interface RecentApplication {
    _id: string;
    jobTitle: string;
    applicantName: string;
    status: string;
    appliedAt: string;
    amount: number;
}

const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ComponentType<any>;
    change?: number;
    changeType?: 'increase' | 'decrease' | 'neutral';
    color: string;
}> = ({ title, value, icon: Icon, change, changeType = 'neutral', color }) => {
    const getChangeIcon = () => {
        if (!change) return null;
        if (changeType === 'increase') return <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />;
        if (changeType === 'decrease') return <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />;
        return <div className="h-4 w-4" />;
    };

    const getChangeColor = () => {
        if (changeType === 'increase') return 'text-green-600 dark:text-green-400';
        if (changeType === 'decrease') return 'text-red-600 dark:text-red-400';
        return 'text-gray-600 dark:text-gray-400';
    };

    return (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 p-6 shadow-lg border border-gray-200/50 dark:border-gray-600/50 backdrop-blur-sm">
            <div className="absolute top-0 right-0 h-20 w-20 opacity-5">
                <Icon className="h-20 w-20" />
            </div>
            <div className="relative">
                <div className="flex items-center justify-between">
                    <div className={`p-3 rounded-xl ${color} shadow-lg`}>
                        <Icon className="h-6 w-6 text-white" />
                    </div>
                    {change !== undefined && (
                        <div className={`flex items-center space-x-1 ${getChangeColor()} text-sm font-medium`}>
                            {getChangeIcon()}
                            <span>{Math.abs(change)}%</span>
                        </div>
                    )}
                </div>
                <div className="mt-4">
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">{value}</p>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mt-1">{title}</p>
                </div>
            </div>
        </div>
    );
};

const CompanyDashboard: React.FC = () => {
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState<CompanyStats>({
        totalJobs: 0,
        activeJobs: 0,
        totalApplications: 0,
        pendingApplications: 0,
        approvedApplications: 0,
        totalTeamMembers: 0,
        totalBudgetSpent: 0,
        monthlySpent: 0,
        jobViews: 0,
        conversionRate: 0,
    });
    const [recentJobs, setRecentJobs] = useState<RecentJob[]>([]);
    const [recentApplications, setRecentApplications] = useState<RecentApplication[]>([]);

    const fetchDashboardData = useCallback(async () => {
        try {
            setLoading(true);

            // Fetch company stats
            const statResponse = await api.get('/companies/dashboard-stats');
            setStats(statResponse.data.stats);

            // Fetch recent jobs
            const jobsResponse = await api.get('/companies/jobs/recent');
            setRecentJobs(jobsResponse.data.jobs);

            // Fetch recent applications
            const applicationsResponse = await api.get('/companies/applications/recent');
            setRecentApplications(applicationsResponse.data.applications);

        } catch (error: any) {
            console.error('Dashboard data fetch error:', error);
            const errorMessage = error.response?.data?.message || 'Failed to fetch dashboard data';
            dispatch(showToast({
                message: errorMessage,
                type: 'error'
            }));
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case 'approved': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'completed': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'active': return <ClockIcon className="h-4 w-4" />;
            case 'pending': return <ClockIcon className="h-4 w-4" />;
            case 'approved': return <CheckCircleIcon className="h-4 w-4" />;
            case 'completed': return <CheckCircleIcon className="h-4 w-4" />;
            default: return <XCircleIcon className="h-4 w-4" />;
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white shadow-xl">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold">Welcome back, {(user as any)?.companyName || 'Company'}</h1>
                        <p className="text-blue-100 mt-2 text-lg">Manage your company operations and track performance</p>
                    </div>
                    <div className="text-right">
                        <p className="text-blue-100">Today</p>
                        <p className="text-2xl font-bold">{formatDate(new Date().toISOString())}</p>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title="Total Jobs"
                    value={stats.totalJobs}
                    icon={BriefcaseIcon}
                    color="bg-blue-500"
                />
                <StatCard
                    title="Active Jobs"
                    value={stats.activeJobs}
                    icon={CheckCircleIcon}
                    color="bg-green-500"
                />
                <StatCard
                    title="Applications"
                    value={stats.totalApplications}
                    icon={UsersIcon}
                    color="bg-purple-500"
                />
                <StatCard
                    title="Team Members"
                    value={stats.totalTeamMembers}
                    icon={UsersIcon}
                    color="bg-orange-500"
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title="Monthly Spent"
                    value={formatCurrency(stats.monthlySpent)}
                    icon={CurrencyDollarIcon}
                    color="bg-green-500"
                />
                <StatCard
                    title="Total Budget"
                    value={formatCurrency(stats.totalBudgetSpent)}
                    icon={ArrowTrendingUpIcon}
                    color="bg-indigo-500"
                />
                <StatCard
                    title="Job Views"
                    value={stats.jobViews.toLocaleString()}
                    icon={EyeIcon}
                    color="bg-pink-500"
                />
                <StatCard
                    title="Conversion Rate"
                    value={`${stats.conversionRate}%`}
                    icon={ChartBarIcon}
                    color="bg-teal-500"
                />
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Jobs */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                            <BriefcaseIcon className="h-5 w-5 mr-2 text-blue-600" />
                            Recent Jobs
                        </h3>
                    </div>
                    <div className="p-6 space-y-4">
                        {recentJobs.length === 0 ? (
                            <div className="text-center py-8">
                                <BriefcaseIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 dark:text-gray-400">No jobs posted yet</p>
                            </div>
                        ) : (
                            recentJobs.map((job) => (
                                <div key={job._id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                                    <div className="flex-1">
                                        <h4 className="font-medium text-gray-900 dark:text-white">{job.title}</h4>
                                        <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                                            <span className="flex items-center">
                                                <EyeIcon className="h-4 w-4 mr-1" />
                                                {job.views} views
                                            </span>
                                            <span className="flex items-center">
                                                <UsersIcon className="h-4 w-4 mr-1" />
                                                {job.applications} applications
                                            </span>
                                            <span>{formatDate(job.createdAt)}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)} flex items-center`}>
                                            {getStatusIcon(job.status)}
                                            <span className="ml-1 capitalize">{job.status}</span>
                                        </span>
                                        <div className="text-right">
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(job.budgetSpent)}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">spent</p>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Recent Applications */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-green-50 to-blue-50 dark:from-gray-800 dark:to-gray-700">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                            <UsersIcon className="h-5 w-5 mr-2 text-green-600" />
                            Recent Applications
                        </h3>
                    </div>
                    <div className="p-6 space-y-4">
                        {recentApplications.length === 0 ? (
                            <div className="text-center py-8">
                                <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 dark:text-gray-400">No applications received yet</p>
                            </div>
                        ) : (
                            recentApplications.map((application) => (
                                <div key={application._id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                                    <div className="flex-1">
                                        <h4 className="font-medium text-gray-900 dark:text-white">{application.jobTitle}</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">by {application.applicantName}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{formatDate(application.appliedAt)}</p>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(application.status)} flex items-center`}>
                                            {getStatusIcon(application.status)}
                                            <span className="ml-1 capitalize">{application.status}</span>
                                        </span>
                                        <div className="text-right">
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(application.amount)}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">amount</p>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <button className="flex items-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors">
                        <BriefcaseIcon className="h-8 w-8 text-blue-600 mr-3" />
                        <div className="text-left">
                            <p className="font-medium text-blue-900 dark:text-blue-100">Post New Job</p>
                            <p className="text-sm text-blue-700 dark:text-blue-200">Create job posting</p>
                        </div>
                    </button>
                    <button className="flex items-center p-4 bg-green-50 dark:bg-green-900/20 rounded-xl hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors">
                        <UsersIcon className="h-8 w-8 text-green-600 mr-3" />
                        <div className="text-left">
                            <p className="font-medium text-green-900 dark:text-green-100">Manage Team</p>
                            <p className="text-sm text-green-700 dark:text-green-200">Add team members</p>
                        </div>
                    </button>
                    <button className="flex items-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors">
                        <ClipboardDocumentListIcon className="h-8 w-8 text-purple-600 mr-3" />
                        <div className="text-left">
                            <p className="font-medium text-purple-900 dark:text-purple-100">Review Applications</p>
                            <p className="text-sm text-purple-700 dark:text-purple-200">Process applications</p>
                        </div>
                    </button>
                    <button className="flex items-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors">
                        <ChartBarIcon className="h-8 w-8 text-orange-600 mr-3" />
                        <div className="text-left">
                            <p className="font-medium text-orange-900 dark:text-orange-100">View Analytics</p>
                            <p className="text-sm text-orange-700 dark:text-orange-200">Track performance</p>
                        </div>
                    </button>
                </div>
            </div>

            <Toast />
        </div>
    );
};

export default CompanyDashboard;